"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useRevieve = void 0;
var _favoritesHeart = require("@ori/favorites-heart");
var _presentationUtils = require("@ori/presentation-utils");
var _react = require("react");
var _constants = require("../../constants");
var _hooks = require("../../hooks");
var _models = require("../../models");
var _utils = require("../../utils");
var _constants2 = require("./constants");
var _utils2 = require("./utils");
const useRevieve = ({
  novageSkincareConfiguration,
  openQueryParam
}) => {
  const {
    environment,
    partnerId,
    recommendationPageEnabled
  } = novageSkincareConfiguration;
  const canChangeOpenQueryParam = openQueryParam !== _constants.OPEN_QUERY_PARAM_ALWAYS;
  const {
    onFavoritesClick
  } = (0, _favoritesHeart.useFavorites)();
  const redirectToPdp = (0, _hooks.useRedirectToPdp)();
  const locale = (0, _hooks.useLocale)();
  const [state, setState] = (0, _react.useState)(_constants2.STATE_DEFAULT);
  const {
    close,
    open,
    reset
  } = (0, _hooks.useRevieveAdvisor)({
    novageSkincareConfiguration
  });
  const {
    restoreProduct,
    storeProduct
  } = (0, _hooks.useSessionStorage)({
    novageSkincareConfiguration
  });
  const clearOpenQueryParam = (0, _react.useCallback)(() => {
    if (canChangeOpenQueryParam) {
      (0, _utils.removeOpenQueryParam)();
    }
  }, [canChangeOpenQueryParam]);
  const setupOpenQueryParam = (0, _react.useCallback)(() => {
    if (canChangeOpenQueryParam) {
      (0, _utils.setOpenQueryParam)();
    }
  }, [canChangeOpenQueryParam]);
  const continueToPdp = (0, _react.useCallback)(() => {
    const product = restoreProduct();
    if (product) {
      redirectToPdp({
        product
      });
    }
  }, [redirectToPdp, restoreProduct]);
  const handleClose = (0, _react.useCallback)(() => {
    setState(_constants2.STATE_DEFAULT);
    clearOpenQueryParam();
  }, [clearOpenQueryParam]);
  const handleOpen = (0, _react.useCallback)(() => {
    setupOpenQueryParam();
    if (restoreProduct()) {
      setState(_constants2.STATE_RESTORED);
    } else {
      open();
    }
  }, [setupOpenQueryParam, open, restoreProduct]);
  const toggleFavorite = (0, _react.useCallback)(({
    id
  }) => {
    onFavoritesClick({
      productCode: id
    });
  }, [onFavoritesClick]);
  const initConfig = (0, _react.useMemo)(() => {
    const disableAnalytics = !(0, _utils.isAnalyticsConsentGranted)();
    return {
      env: environment,
      locale,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- 3rd party naming
      partner_id: partnerId,
      analytics: {
        disableUsageAnalytics: disableAnalytics,
        disableProductAnalytics: disableAnalytics,
        disableErrorReporting: disableAnalytics
      },
      onClose: handleClose,
      onAddToCart: data => {
        if (Array.isArray(data)) {
          data.forEach(_utils2.addToBasket);
        } else {
          (0, _utils2.addToBasket)(data);
        }
      },
      onAddToFavorites: toggleFavorite,
      onClickProduct: (product, source) => {
        if (source === _models.ClickProductSource.productList || source === _models.ClickProductSource.expandedProductCard) {
          setupOpenQueryParam();
          storeProduct(product);
          redirectToPdp({
            product,
            newTab: true
          });
        }
      },
      onCheckout: () => {
        const url = (0, _presentationUtils.getCurrentUrl)();
        url.pathname = '/shopping/basket';
        url.searchParams.delete(_constants.OPEN_QUERY_PARAM);
        window.location.assign(url);
      },
      onDisplayProducts: (historyId, products) => {
        if (recommendationPageEnabled) {
          return;
        }
        const product = products[0];
        if (product) {
          setupOpenQueryParam();
          storeProduct(product);
          redirectToPdp({
            product,
            delay: 2000
          });
        }
      },
      onTrackingEvent: _utils2.sendUserEvents,
      onFetchProducts: () => {
        if (recommendationPageEnabled) {
          return;
        }
        close();
        setState(_constants2.STATE_LOADING);
      },
      onRemoveFromFavorites: toggleFavorite
    };
  }, [close, environment, handleClose, locale, partnerId, recommendationPageEnabled, redirectToPdp, setupOpenQueryParam, storeProduct, toggleFavorite]);
  const onInit = (0, _react.useCallback)(() => {
    if (openQueryParam) {
      handleOpen();
    }
  }, [handleOpen, openQueryParam]);
  const handleReset = (0, _react.useCallback)(() => {
    setState(_constants2.STATE_DEFAULT);
    reset(initConfig);
    handleOpen();
  }, [handleOpen, initConfig, reset]);
  return (0, _react.useMemo)(() => ({
    close: handleClose,
    continueToPdp,
    initConfig,
    onInit,
    open: handleOpen,
    reset: handleReset,
    state
  }), [continueToPdp, handleClose, handleOpen, handleReset, initConfig, onInit, state]);
};
exports.useRevieve = useRevieve;